/* ------------------------------------------------------------------------
   Gift Card product page — sections/gift-card.liquid

   Owns everything the two gift-card formats need client-side:
     - denomination tiles + custom amount
     - recipient form validation ("buying for myself" toggle included)
     - live digital-card amount preview

   Self-contained: no dependency on theme.js beyond the `variant:changed`
   event it dispatches and the `.ProductMeta__Price` node it updates. The
   "Card format" toggle itself is a plain link (see
   snippets/gift-card-format-toggle.liquid) — no JS involved.
   ------------------------------------------------------------------------ */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-section-type="product"]';

  function currencyPrefix(root) {
    var tile = root.querySelector('.GiftCardTile__Amount');
    if (tile) {
      var m = tile.textContent.match(/^[^\d]*/);
      if (m && m[0].trim()) return m[0];
    }
    return '₹';
  }

  function money(root, n) {
    var num = parseInt(n, 10);
    var grouped = isFinite(num) ? num.toLocaleString('en-IN') : String(n);
    return currencyPrefix(root) + grouped;
  }

  /* ----------------------------------------------------------------
     Denominations + custom amount
     ---------------------------------------------------------------- */
  function initDenominations(root) {
    var wrap = root.querySelector('.GiftCardOptions');
    if (!wrap) return;

    var min = parseInt(wrap.getAttribute('data-gc-min'), 10) || 0;
    var formId = wrap.getAttribute('data-gc-form');
    var form = (formId && root.querySelector('#' + CSS.escape(formId))) || wrap.closest('form');
    var panel = root.querySelector('[id^="gc-custom-panel-"]');
    var input = root.querySelector('[id^="gc-custom-input-"]');
    var prop = root.querySelector('[id^="gc-custom-prop-"]');
    var rawProp = root.querySelector('[id^="gc-custom-raw-"]');
    var errorEl = root.querySelector('[id^="gc-custom-error-"]');
    var radios = wrap.querySelectorAll('.GiftCardTile__Radio');
    var errorMsg = wrap.getAttribute('data-gc-error') || ('Enter a whole amount of ' + money(root, min) + ' or more');
    var customMode = false;

    function setPrice(text) {
      var el = root.querySelector('.ProductMeta__Price');
      if (el) el.textContent = text;
    }

    function updateProp() {
      var v = parseInt(input.value, 10);
      if (v && v >= min && String(v) === input.value.trim()) {
        prop.value = money(root, v);
        if (rawProp) rawProp.value = String(v);
        setPrice(money(root, v));
        return true;
      }
      prop.value = '';
      if (rawProp) rawProp.value = '';
      return false;
    }

    function enterCustom() {
      customMode = true;
      panel.hidden = false;
      prop.disabled = false;
      if (rawProp) rawProp.disabled = false;
      if (input.value) updateProp();
      else { setPrice(money(root, min)); input.focus(); }
    }

    function exitCustom() {
      customMode = false;
      panel.hidden = true;
      prop.disabled = true;
      prop.value = '';
      if (rawProp) { rawProp.disabled = true; rawProp.value = ''; }
      if (errorEl) errorEl.hidden = true;
    }

    radios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (r.getAttribute('data-gc-custom') === 'true') enterCustom();
        else exitCustom();
      });
    });

    if (input) {
      input.addEventListener('input', function () {
        if (!customMode) return;
        var ok = updateProp();
        if (errorEl) {
          var typed = input.value.trim();
          if (!ok && typed) {
            errorEl.textContent = errorMsg;
            errorEl.hidden = false;
          } else {
            errorEl.hidden = true;
          }
        }
      });
    }

    root.addEventListener('variant:changed', function () {
      if (!customMode) return;
      setTimeout(function () {
        var v = parseInt(input.value, 10);
        setPrice(v && v >= min ? money(root, v) : money(root, min));
      }, 0);
    });

    function validate(e) {
      if (!customMode) return;
      if (!updateProp()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (errorEl) { errorEl.textContent = errorMsg; errorEl.hidden = false; }
        input.focus();
      }
    }

    if (form) form.addEventListener('submit', validate);
    // querySelectorAll on `root` (not just `form`/`(form || root)`'s first
    // match) so this also covers the page's own Add to Cart button when it
    // lives outside the <form> DOM subtree.
    root.querySelectorAll('[data-action="add-to-cart"]').forEach(function (atc) {
      atc.addEventListener('click', validate);
    });

    if (wrap.querySelector('.GiftCardTile__Radio[data-gc-custom="true"]:checked')) enterCustom();
  }

  /* ----------------------------------------------------------------
     Recipient form: "buying for myself" + validation
     ---------------------------------------------------------------- */
  function initRecipientForm(root) {
    var wrap = root.querySelector('.GiftCardRecipient');
    if (!wrap) return;

    var formId = wrap.getAttribute('data-gc-form');
    var form = (formId && root.querySelector('#' + CSS.escape(formId))) || wrap.closest('form');

    var selfCheckbox = wrap.querySelector('.GiftCardRecipient__Checkbox');
    var recipientFields = wrap.querySelector('[data-gc-recipient-fields]');
    var sendToRecipientInput = wrap.querySelector('[name="properties[__shopify_send_gift_card_to_recipient]"]');
    var nameField = wrap.querySelector('[id^="gc-recipient-name-"]');
    var emailField = wrap.querySelector('[id^="gc-recipient-email-"]');

    function applySelfPurchase() {
      var buyingForSelf = !!(selfCheckbox && selfCheckbox.checked);

      if (recipientFields) recipientFields.hidden = buyingForSelf;

      [nameField, emailField].forEach(function (field) {
        if (!field) return;
        if (buyingForSelf) {
          field.removeAttribute('required');
          field.classList.remove('gc-required-field', 'gc-field-invalid');
          field.value = '';
        } else {
          field.classList.add('gc-required-field');
          field.setAttribute('required', 'required');
        }
      });

      if (sendToRecipientInput) sendToRecipientInput.value = buyingForSelf ? '' : 'on';
    }

    if (selfCheckbox) {
      selfCheckbox.addEventListener('change', applySelfPurchase);
      applySelfPurchase();
    }

    var offsetField = wrap.querySelector('[id^="gc-offset-"]');
    if (offsetField) offsetField.value = new Date().getTimezoneOffset();

    // The Liquid-rendered `min` on the delivery-date field is today in the
    // shop's timezone; override it with today in the shopper's own local
    // timezone so a shopper west of the shop (for whom the shop's "today"
    // hasn't started yet) isn't shown a min date that's still in their past,
    // and one east of it can't pick a date the shop would already call
    // yesterday.
    var sendOnField = wrap.querySelector('[id^="gc-send-on-"]');
    if (sendOnField) {
      var now = new Date();
      var localToday = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 10);
      sendOnField.min = localToday;
      // Default to today (also the field's floor) rather than leaving it
      // blank, matching the helper text below it ("Leave as today…") —
      // still fully editable, and re-clamped if a restored/typed value is
      // now in the past.
      if (!sendOnField.value || sendOnField.value < localToday) sendOnField.value = localToday;
    }

    var variantIdField = wrap.querySelector('[id^="gc-variant-id-"]');
    function syncVariantId() {
      if (!variantIdField || !form) return;
      var idField = form.querySelector('[name="id"]');
      if (idField && idField.value) variantIdField.value = idField.value;
    }
    if (variantIdField && form) {
      syncVariantId();
      form.addEventListener('change', function () { setTimeout(syncVariantId, 50); });
    }

    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    function validate(e) {
      var valid = true;
      wrap.querySelectorAll('.gc-required-field').forEach(function (field) {
        if (field.closest('[hidden]')) return; // skip fields hidden by "buying for myself"
        var error = field.parentNode.querySelector('.GiftCardRecipient__Error');
        field.classList.remove('gc-field-invalid');
        if (error) error.style.display = 'none';

        var val = field.value.trim();
        var empty = !val;
        var badEmail = field.classList.contains('gc-email-field') && !empty && !emailRegex.test(val);

        if (empty || badEmail) {
          valid = false;
          field.classList.add('gc-field-invalid');
          if (error) error.style.display = 'block';
        }
      });

      if (!valid) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var first = wrap.querySelector('.gc-field-invalid');
        if (first) first.focus();
      }
    }

    if (form) {
      form.addEventListener('submit', validate);
      // Same reasoning as initDenominations above: query `root`, not just
      // `form`, in case the Add to Cart button lives outside the <form>
      // DOM subtree.
      root.querySelectorAll('[data-action="add-to-cart"]').forEach(function (atc) {
        atc.addEventListener('click', validate);
      });
    }
  }

  /* ----------------------------------------------------------------
     Live digital-card amount preview
     ---------------------------------------------------------------- */
  function initCardPreview(root) {
    var card = root.querySelector('.GcCard');
    if (!card) return;

    // There are two card faces once the email-preview modal exists (the
    // gallery-column card and the one rendered inside the modal, both from
    // snippets/gift-card-card.liquid) — keep every one of them in sync.
    var amountEls = root.querySelectorAll('[data-gc-preview="amount"]');

    function getAmount() {
      var scope = root.querySelector('.GiftCardOptions');
      var checked = scope && scope.querySelector('.GiftCardTile__Radio:checked');
      if (checked) {
        if (checked.getAttribute('data-gc-custom') === 'true') {
          var custom = root.querySelector('[id^="gc-custom-input-"]');
          var v = custom ? String(custom.value).trim() : '';
          if (v) return money(root, v);
        } else {
          var lbl = checked.parentNode.querySelector('.GiftCardTile__Amount');
          if (lbl) return lbl.textContent.trim();
        }
      }
      var price = root.querySelector('.ProductMeta__Price');
      return price ? price.textContent.trim() : '';
    }

    // The card face uses a Didot/Georgia serif stack (assets/gift-card.css
    // .GcCard__amount) that doesn't reliably carry a ₹ glyph, unlike the
    // rest of the page (tiles, custom input, price) which intentionally use
    // ₹ — swap only here, on the rendered card, to "Rs." instead.
    function toCardCurrency(amount) {
      return amount.replace(/^\s*₹\s*/, 'Rs. ');
    }

    function update() {
      var amount = getAmount();
      var text = amount ? toCardCurrency(amount) : '—';
      amountEls.forEach(function (el) { el.textContent = text; });
    }

    // 'change' covers the denomination radios; the custom-amount field needs
    // 'input' too, since text/number inputs only fire 'change' on blur —
    // without this the preview only updated once focus left the field.
    root.addEventListener('change', function () { setTimeout(update, 30); });
    root.addEventListener('input', function () { setTimeout(update, 30); });
    root.addEventListener('variant:changed', update);
    update();
  }

  /* ----------------------------------------------------------------
     Email preview modal — "View Digital Card" opens a modal previewing
     the email the recipient will receive, filled in live from whatever
     the shopper has typed into the recipient form so far. The amount
     shown on the modal's card face is kept in sync by initCardPreview
     above (it targets every [data-gc-preview="amount"] node, modal
     included), so this only needs to populate the name/message fields.
     ---------------------------------------------------------------- */
  function initEmailPreviewModal(root) {
    var trigger = root.querySelector('[data-gc-modal-open]');
    if (!trigger) return;

    var targetId = trigger.getAttribute('data-gc-modal-open');
    var modal = targetId ? root.querySelector('#' + CSS.escape(targetId)) : null;
    if (!modal) return;

    var nameField = root.querySelector('[id^="gc-recipient-name-"]');
    var messageField = root.querySelector('[id^="gc-message-"]');

    var recipientEl = modal.querySelector('[data-gc-email="recipient"]');
    var messageEl = modal.querySelector('[data-gc-email="message"]');
    var messageWrapEl = modal.querySelector('[data-gc-email="message-wrap"]');

    function populate() {
      if (recipientEl) {
        var name = nameField ? nameField.value.trim() : '';
        recipientEl.textContent = name || 'you';
      }
      var message = messageField ? messageField.value.trim() : '';
      if (messageEl) messageEl.textContent = message;
      if (messageWrapEl) messageWrapEl.hidden = !message;
    }

    var escHandler = null;

    function close() {
      // Matches how every other popup in the theme opens/closes (e.g.
      // .notify-drawer.is-open in assets/theme.css) — the modal stays in
      // the DOM and is toggled via class + aria-hidden so it can animate
      // in/out instead of popping via [hidden].
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      // Matches every other drawer/modal in this theme (assets/theme.js) —
      // .no-scroll is a shared `overflow: hidden !important` utility on
      // <html>, not <body>, since iOS Safari needs the lock on the root
      // element to actually stop background scroll.
      document.documentElement.classList.remove('no-scroll');
      if (escHandler) {
        document.removeEventListener('keydown', escHandler);
        escHandler = null;
      }
      trigger.focus();
    }

    function open() {
      populate();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('no-scroll');
      escHandler = function (e) {
        if (e.key === 'Escape') close();
      };
      document.addEventListener('keydown', escHandler);
      var closeButton = modal.querySelector('.GcModal__CloseButton');
      if (closeButton) closeButton.focus();
    }

    trigger.addEventListener('click', open);
    modal.querySelectorAll('[data-gc-modal-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
  }

  /* ----------------------------------------------------------------
     Card format toggle — swap the section in place via the Section
     Rendering API instead of a full page navigation.

     The physical gallery itself needs no init call here: this section's
     <section> tag carries data-section-type="product", so the theme's
     global ProductSection (assets/theme.js) already Flickity-initializes
     it on page load. After an AJAX swap we re-arm that same lifecycle by
     dispatching shopify:section:load (see swapTo/popstate below) instead
     of duplicating ProductSection's own gallery/zoom/nav logic here.
     ---------------------------------------------------------------- */
  function initFormatToggle(root) {
    var buttons = root.querySelectorAll('.GiftCardToggle__Button');
    if (!buttons.length) return;

    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    // Both gift card templates render their "main" section under the same
    // section.id ("main"), so a plain id -> value map round-trips cleanly
    // when restoring what the shopper had already typed.
    function snapshotValues(scope) {
      var values = {};
      scope.querySelectorAll('input[id], textarea[id]').forEach(function (field) {
        if (field.type === 'checkbox' || field.type === 'radio') return;
        values[field.id] = field.value;
      });
      return values;
    }

    function restoreValues(scope, values) {
      scope.querySelectorAll('input[id], textarea[id]').forEach(function (field) {
        if (field.type === 'checkbox' || field.type === 'radio') return;
        if (values[field.id]) field.value = values[field.id];
      });
    }

    function swapTo(url) {
      if (!url || url === '#') return;

      var separator = url.indexOf('?') === -1 ? '?' : '&';
      var fetchUrl = url + separator + 'section_id=' + encodeURIComponent(sectionId);
      var savedValues = snapshotValues(root);

      root.classList.add('is-loading');

      fetch(fetchUrl, { credentials: 'same-origin' })
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var newRoot = doc.querySelector(ROOT_SELECTOR);
          if (!newRoot) { window.location.href = url; return; }

          dispatchSectionUnload(root, sectionId);
          root.replaceWith(newRoot);
          history.pushState({ giftCardFormat: true }, '', url);
          restoreValues(newRoot, savedValues);
          // dispatchSectionLoad already re-triggers this file's own
          // "shopify:section:load" listener (bottom of this file), which
          // calls init(newRoot) for us. Calling init(newRoot) again here
          // would bind every handler (including the format-toggle buttons'
          // click listener) a second time — on the next click that fires
          // two concurrent swaps, and the loser's dispatchSectionUnload
          // tears down the winner's freshly-created ProductSection
          // (Flickity + zoom) with nothing left to recreate it, since both
          // templates share the same static section id ("main").
          dispatchSectionLoad(newRoot);
          bindPostSwapAccordions(newRoot);
        })
        .catch(function () {
          // Fall back to a normal navigation if the fetch failed.
          window.location.href = url;
        });
    }

    buttons.forEach(function (button) {
      button.addEventListener('click', function (e) {
        if (button.classList.contains('is-active')) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        swapTo(button.getAttribute('href'));
      });
    });
  }

  // Shopify's own theme editor dispatches "shopify:section:load" on the
  // #shopify-section-{id} wrapper *around* the section — its listener
  // (SectionContainer._onSectionLoad in assets/theme.js) does
  // event.target.querySelector("[data-section-id]"), a descendant search,
  // so this must fire on newRoot's parent, not on newRoot itself. That's
  // what re-arms ProductSection (Flickity gallery, nav, zoom) on the
  // markup we just swapped in, without duplicating any of its logic here.
  function dispatchSectionLoad(newRoot) {
    var wrapper = newRoot.parentElement;
    if (!wrapper) return;
    wrapper.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
  }

  // Companion to dispatchSectionLoad — lets SectionContainer._onSectionUnload
  // (assets/theme.js) run the outgoing ProductSection's onUnload() and drop
  // it from its instances list, instead of leaking a stale entry (keyed by
  // the same static section.id "main") on every format-toggle swap.
  // _onSectionUnload reads event.detail.sectionId directly (no DOM lookup),
  // so this can fire on any element — the still-attached old root is fine.
  function dispatchSectionUnload(oldRoot, sectionId) {
    oldRoot.dispatchEvent(new CustomEvent('shopify:section:unload', {
      bubbles: true,
      detail: { sectionId: sectionId },
    }));
  }

  // assets/custom.js binds the product-accordion's "panel-heading" toggle
  // and "read more/less" truncation with plain (non-delegated) jQuery
  // handlers, bound once to whatever elements exist at DOMContentLoaded — it
  // never sees elements introduced later by our replaceWith. Re-bind the
  // exact same behavior here, scoped to the freshly swapped-in root only
  // (initial-page-load elements keep working via custom.js's own binding
  // untouched — this never runs at that point, only after a swap, so
  // there's no risk of double-binding/double-toggling).
  function bindPostSwapAccordions(root) {
    var $ = window.jQuery;
    if (!$) return;

    $(root).find('.panel-heading').each(function () {
      var $heading = $(this);
      if ($heading.data('gcBound')) return;
      $heading.data('gcBound', true);
      $heading.on('click', function () {
        var $this = $(this);
        $this.toggleClass('open').next('.panel-content').slideToggle();
        $this.parent('.pannel-tab').siblings().children('.panel-content').slideUp();
        $this.parent('.pannel-tab').siblings().children('.panel-heading').removeClass('open');
      });
    });

    var $containers = $(root).find('.product_description-accordion');
    var $readMoreBtns = $(root).find('.read-more-btn');
    var $readLessBtns = $(root).find('.read-less-btn');

    $readMoreBtns.each(function () {
      var $btn = $(this);
      if ($btn.data('gcBound')) return;
      $btn.data('gcBound', true);
      $btn.on('click', function () {
        var index = $readMoreBtns.index(this);
        $containers.css({
          'max-height': '100px',
          '-webkit-mask-image': 'linear-gradient(180deg, #000 50%, transparent 100%)',
        });
        $readMoreBtns.show();
        $readLessBtns.hide();
        $containers.eq(index).css({ 'max-height': 'none', '-webkit-mask-image': 'none' });
        $readMoreBtns.eq(index).hide();
        $readLessBtns.eq(index).show();
      });
    });

    $readLessBtns.each(function () {
      var $btn = $(this);
      if ($btn.data('gcBound')) return;
      $btn.data('gcBound', true);
      $btn.on('click', function () {
        var index = $readLessBtns.index(this);
        $containers.eq(index).css({
          'max-height': '100px',
          '-webkit-mask-image': 'linear-gradient(180deg, #000 50%, transparent 100%)',
        });
        $readMoreBtns.eq(index).show();
        $readLessBtns.eq(index).hide();
        $('html, body').animate({ scrollTop: $containers.eq(index).offset().top }, 500);
      });
    });
  }

  window.addEventListener('popstate', function () {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root || !root.querySelector('.GiftCardOptions')) return;

    var sectionId = root.getAttribute('data-section-id');

    fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId), {
      credentials: 'same-origin',
    })
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newRoot = doc.querySelector(ROOT_SELECTOR);
        if (!newRoot) return;
        dispatchSectionUnload(root, sectionId);
        root.replaceWith(newRoot);
        // See the matching comment in swapTo() above — dispatchSectionLoad
        // already re-runs init(newRoot) via this file's own
        // "shopify:section:load" listener; calling it again here would
        // double-bind every handler on newRoot.
        dispatchSectionLoad(newRoot);
        bindPostSwapAccordions(newRoot);
      });
  });

  function init(root) {
    if (!root || !root.querySelector('.GiftCardOptions')) return;
    initDenominations(root);
    initRecipientForm(root);
    initCardPreview(root);
    initEmailPreviewModal(root);
    initFormatToggle(root);
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach(init);
  });

  window.GiftCardProduct = { init: init };
})();
